import type { WizardFlowDefinition } from '../flowTypes';
import DriverConsentStartStep from '../../components/driverConsent/DriverConsentStartStep';
import GdprStep from '../../components/shared/GdprStep';
import ConsentSelectChildStep from '../../components/consent/ConsentSelectChildStep';
import ConsentEnterChildStep from '../../components/consent/ConsentEnterChildStep';
import DriverConsentGuardianStep from '../../components/driverConsent/DriverConsentGuardianStep';
import DriverConsentNotifyStep from '../../components/driverConsent/DriverConsentNotifyStep';
import DriverConsentGuardianContactStep from '../../components/driverConsent/DriverConsentGuardianContactStep';
import DriverConsentSummaryStep from '../../components/driverConsent/DriverConsentSummaryStep';
import DriverParentSignatureStep from '../../components/driver/DriverParentSignatureStep';
import DriverConsentConfirmationStep from '../../components/driverConsent/DriverConsentConfirmationStep';
import type { SelfServiceData } from '../../types';

const resolveChildStep = (data: SelfServiceData): 'consent_enter_child' | 'dlc_guardian' => {
  if (data.consentManualChild === true) {
    return 'consent_enter_child';
  }
  if (!data.consentChildName?.trim() && !data.consentChildCpr?.trim()) {
    return 'consent_enter_child';
  }
  return 'dlc_guardian';
};

const driverConsentPhases = [
  { id: 'intro', name: 'Start' },
  { id: 'details', name: 'Oplysninger' },
  { id: 'review', name: 'Gennemse' },
  { id: 'completion', name: 'Afslut' }
] as const;

const driverConsentFlow: WizardFlowDefinition = {
  id: 'koerekort_samtykke',
  startStep: 'dlc_start',
  phases: driverConsentPhases,
  steps: {
    dlc_start: { id: 'dlc_start', name: 'Start', component: DriverConsentStartStep, next: 'gdpr', phase: 'intro' },
    gdpr: { id: 'gdpr', name: 'GDPR', component: GdprStep, next: 'consent_select_child', phase: 'intro' },
    consent_select_child: {
      id: 'consent_select_child',
      name: 'Vælg barn',
      component: ConsentSelectChildStep,
      getNext: resolveChildStep,
      validate: (data) => {
        if (data.consentManualChild === undefined && !data.consentChildName?.trim() && !data.consentChildCpr?.trim()) {
          return "Vælg barn eller vælg \"Et andet barn\".";
        }
        return null;
      },
      phase: 'details'
    },
    consent_enter_child: {
      id: 'consent_enter_child',
      name: 'Angiv barn',
      component: ConsentEnterChildStep,
      phase: 'details'
    },
    dlc_guardian: {
      id: 'dlc_guardian',
      name: 'Forældre',
      component: DriverConsentGuardianStep,
      getNext: (data) => (data.dlCustodyType === 'med' ? 'dlc_notify' : 'dlc_summary'),
      phase: 'details'
    },
    dlc_notify: {
      id: 'dlc_notify',
      name: 'Digital Post',
      component: DriverConsentNotifyStep,
      getNext: (data) => (data.dlNotifySecondGuardian === true ? 'dlc_guardian_contact' : 'dlc_summary'),
      phase: 'details'
    },
    dlc_guardian_contact: {
      id: 'dlc_guardian_contact',
      name: 'Kontaktinfo',
      component: DriverConsentGuardianContactStep,
      next: 'dlc_summary',
      phase: 'details'
    },
    dlc_summary: {
      id: 'dlc_summary',
      name: 'Oversigt',
      component: DriverConsentSummaryStep,
      next: 'dl_parent_signature',
      phase: 'review'
    },
    dl_parent_signature: {
      id: 'dl_parent_signature',
      name: 'Underskrift',
      component: DriverParentSignatureStep,
      next: 'dlc_confirmation',
      phase: 'completion'
    },
    dlc_confirmation: {
      id: 'dlc_confirmation',
      name: 'Bekræftelse',
      component: DriverConsentConfirmationStep,
      phase: 'completion'
    }
  }
};

export { driverConsentFlow };
